'use client';

import { useStore } from '@/store/store';
import { usePopupBehavior } from '@/hooks/use-popup-behavior';
import { PopupHeader } from './components/popup-header';
import { PopupContent } from './components/popup-content';
import { TrailerSection } from './components/trailer-section';
import type { ExclusivePopupContent } from './utils/get-popup-content';
import { classes } from '@/utils/classes/classes';
import BadgeFactoryClient from '@/app/components/badge/badge-factory.client';
import { TextBadge } from '@/app/components/badge/layouts/text/text-badge';
import './styles/default.scss';
import { EXCLUSIVE_ZONE_NAMES, SLICE_TYPE, EXCLUSIVE_LAUNCH_TYPE } from '@/utils/constants';
import { type InteractionType, trackExclusiveLaunchEvent } from '@/tracking/amplitude/events/general/exclusives';
import { useEffect } from 'react';
import { getText } from '@/utils/text/text';

export const ExclusivePopup: React.FC = () => {
    const { sendEvent } = useStore((state) => state.tracking);
    const params = useStore((state) => state.popup.activePopup?.params);
    const exclusivePopupContent = params as ExclusivePopupContent;
    const isPostLaunch = exclusivePopupContent?.exclusiveType === SLICE_TYPE.EXCLUSIVE_POST_LAUNCH;
    const isPreLaunch = exclusivePopupContent?.exclusiveType === SLICE_TYPE.EXCLUSIVE_PRE_LAUNCH;
    const isEarlyAccess = exclusivePopupContent?.isEarlyAccess ?? false;
    const exclusiveLaunchType = exclusivePopupContent?.exclusiveLaunchType || '';
    const isEarlyAccessZone = exclusiveLaunchType === EXCLUSIVE_LAUNCH_TYPE.EARLY_ACCESS;
    const exclusiveLaunchTypeClass = exclusiveLaunchType.replace(/_/g, '-').toLowerCase();

    const handleCloseModalTracking = (interactionType: InteractionType, ref: { current: HTMLElement | null }) => {
        const trackingData = trackExclusiveLaunchEvent('gamesExclusiveLaunchModalClosed', ref, {
            interactionType
        });
        sendEvent([trackingData.event]);
    };

    const { fade, popupRef, fadeOutAndClose, handleAnimationEnd, handleClose } = usePopupBehavior(
        exclusivePopupContent?.zoneUid,
        (interactionType) => handleCloseModalTracking(interactionType, popupRef)
    );

    useEffect(() => {
        if (popupRef.current) {
            const trackingData = trackExclusiveLaunchEvent('gamesExclusiveLaunchModalOpened', popupRef);
            sendEvent([trackingData.event]);
        }
    }, [popupRef]);

    if (!exclusivePopupContent) {
        return null;
    }

    const onCtaClick = () => {
        const trackingData = trackExclusiveLaunchEvent('gamesExclusiveLaunchCTAClicked', popupRef);
        sendEvent([trackingData.event]);
        fadeOutAndClose();
    };

    const postLaunchZoneName = isEarlyAccessZone
        ? EXCLUSIVE_ZONE_NAMES.POST_LAUNCH_EARLY_ACCESS
        : EXCLUSIVE_ZONE_NAMES.POST_LAUNCH;

    const preLaunchZoneName = isEarlyAccessZone
        ? EXCLUSIVE_ZONE_NAMES.PRE_LAUNCH_EARLY_ACCESS
        : EXCLUSIVE_ZONE_NAMES.PRE_LAUNCH;

    const zoneName = isPostLaunch ? postLaunchZoneName : preLaunchZoneName;

    return (
        <div
            ref={popupRef}
            className={classes(
                'exclusive-modal-component',
                fade && 'fade-out',
                exclusiveLaunchType && `exclusive-modal-component--${exclusiveLaunchTypeClass}`
            )}
            style={
                {
                    '--background-image': exclusivePopupContent.modalBackgroundImage
                        ? `url(${exclusivePopupContent.modalBackgroundImage})`
                        : ''
                } as React.CSSProperties
            }
            onAnimationEnd={handleAnimationEnd}
            data-t-prepostlaunchmodal={isPostLaunch ? 'post-launch' : 'pre-launch'}
            data-t-module={isPostLaunch ? 'post_launch_pop_up' : 'pre_launch_pop_up'}
            data-t-zonename={zoneName}
            data-t-zonelayout={isPostLaunch ? EXCLUSIVE_ZONE_NAMES.POST_LAUNCH : EXCLUSIVE_ZONE_NAMES.PRE_LAUNCH}
            data-t-gameuid={exclusivePopupContent.gameUid}
            data-t-gamename={exclusivePopupContent.launcherUrlProps.gameName}
            data-t-gameprovideruid={exclusivePopupContent.launcherUrlProps.providerUid}
            data-t-gameposition={1}>
            <div className="exclusive-modal-component__content" data-t-cta-button-text={exclusivePopupContent.ctaText}>
                {isEarlyAccessZone && (
                    <BadgeFactoryClient>
                        <TextBadge label={getText({ text: 'components.badge.earlyAccess' })} />
                    </BadgeFactoryClient>
                )}
                <PopupHeader onClose={handleClose} exclusiveLogo={exclusivePopupContent.exclusivePlaceholderLogo} />

                <PopupContent
                    isPostLaunch={isPostLaunch}
                    description={exclusivePopupContent.descriptionText}
                    announcementImage={exclusivePopupContent.announcementImage}
                    headlineLogo={exclusivePopupContent.headlineLogo}
                    launchDate={exclusivePopupContent.launchDate}
                    counterBackground={exclusivePopupContent.counterBackground}
                    isEarlyAccess={isEarlyAccess}>
                    <TrailerSection
                        isPostLaunch={isPostLaunch}
                        isPreLaunch={isPreLaunch}
                        videoTrailerUrl={exclusivePopupContent.videoTrailerUrl}
                        gameUid={exclusivePopupContent.gameUid}
                        contentId={exclusivePopupContent.contentId}
                        videoPoster={exclusivePopupContent.videoPoster}
                        ctaText={exclusivePopupContent.ctaText}
                        categoryPath={exclusivePopupContent.categoryPath}
                        onCtaClick={onCtaClick}
                        launcherUrlProps={exclusivePopupContent.launcherUrlProps}
                        playNowButtonText={exclusivePopupContent.playNowButtonText}
                        zoneUid={exclusivePopupContent.zoneUid}
                        isEarlyAccess={isEarlyAccess}
                    />
                </PopupContent>

                {exclusivePopupContent.copyrightText && (
                    <div className="copyright-text">{exclusivePopupContent.copyrightText}</div>
                )}
            </div>
        </div>
    );
};
